import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Tooltip } from "@mui/material";
import { format } from "date-fns";
import { Pencil, Trash2, X } from "lucide-react";

import { Button } from "components/ui/Button";
import { Dialog } from "components/ui/Dialog/Dialog";
import { Pill } from "components/ui/Pill";
import { CdmReviewReferences } from "features/cdm/components/CdmReviewReferences";
import { LoadingSection } from "features/cdm/components/LoadingSection/LoadingSection";
import { ReviewAndExamForm } from "features/cdm/components/ReviewAndExamForm/ReviewAndExamForm";
import { PCRS_CONNECTION_REQUIRED_MESSAGE } from "features/cdm/constants/pcrsSchemeCheck";
import { getReviewTypeTitle } from "features/cdm/constants/reviewTypes";
import { views } from "features/cdm/hooks/useView";
import { extractSubmissionError } from "features/cdm/utils/extractSubmissionError";
import {
  isOwnReview,
  NOT_OWNER_MESSAGE,
} from "features/cdm/utils/reviewOwnership";
import { usePatientData } from "features/patients/hooks/usePatientData";
import { useStaffInfo } from "features/practice/hooks/useStaffInfo";
import { formatCdmStatus, getCdmStatusColors } from "utils/cdmStatus";

import { CdmMessagePreview } from "../../../components/CdmMessagePreview/CdmMessagePreview";
import { formatCorrectedDate } from "../../../utils/reviewDate";
import { useGetOCF } from "../../hooks/useGetOCF";
import { OutcomeForm } from "../OCFItemForm/components/OutcomeForm/OutcomeForm";
import { RiskAndAssessmentForm } from "../OCFItemForm/components/RiskAndAssessmentForm/RiskAndAssessmentForm";
import { TestResultsForm } from "../OCFItemForm/components/TestResultsForm/TestResultsForm";
import { useSendOCF } from "../OCFItemForm/hooks/useSendOCF";
import { useValidateOCF } from "../OCFItemForm/hooks/useValidateOCF";
import { OcfEditSteps } from "./OcfEditSteps";
import { OcfInitialStep } from "./OcfInitialStep";
import style from "./OCFPreview.module.css";
import { OcfPreviewContent } from "./OcfPreviewContent";

export const OCFPreview = ({
  item,
  setView,
  patientId,
  startEditing,
  actionsDisabled = false,
}) => {
  const { data: patient } = usePatientData();
  const missingPatientFields = [
    !patient?.ethnicityCode && "Ethnic Group",
    !patient?.address?.country && "Country",
    !patient?.financialClass && "Patient Classification",
  ].filter(Boolean);

  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editActiveStep, setEditActiveStep] = useState(1);
  const [editData, setEditData] = useState(null);

  useEffect(() => {
    if (!item) {
      setView({ name: views.noChronicDiseases, cdmId: null });
    }
  }, [item, setView]);

  const { data, isLoading } = useGetOCF(item?.id);

  const { data: currentStaff } = useStaffInfo();
  const isOwn = isOwnReview({
    doctorId: data?.doctorId,
    doctorName: data?.reviewingDoctor,
    staffId: currentStaff?.staffMember?.id,
    staffName: currentStaff?.staffMember
      ? `${currentStaff.staffMember.firstName} ${currentStaff.staffMember.lastName}`
      : null,
  });

  useEffect(() => {
    if (startEditing && data && item && !isEditing && !actionsDisabled) {
      setEditData({ ...data, reviewType: item.reviewType });
      setEditActiveStep(1);
      setIsEditing(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startEditing, !!data, item?.id]);
  const { mutateAsync: validateOCF } = useValidateOCF({ patientId });
  const { mutateAsync: sendOCF, isPending: isSendingOCF } = useSendOCF({
    patientId,
  });

  if (!item) {
    return null;
  }

  if (!data || isLoading) {
    return <LoadingSection />;
  }

  const handleStartEdit = () => {
    if (actionsDisabled) return;

    setEditData({ ...data, reviewType: item.reviewType });
    setEditActiveStep(1);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditData(null);
    setEditActiveStep(1);
  };

  return (
    <section className={style.wrapper}>
      <div className={style.header}>
        <div className={style.headerInner}>
          <h3>{item.name || getReviewTypeTitle(data.reviewType)}</h3>
          <Pill
            color={getCdmStatusColors(data.status).color}
            bg={getCdmStatusColors(data.status).bg}
            border={getCdmStatusColors(data.status).border}
          >
            {formatCdmStatus(data.status)}
          </Pill>
          <p>{format(new Date(item.date || item.reviewDate), "dd-MM-yyyy")}</p>
          {!!formatCorrectedDate(data.correctedDate ?? item.correctedDate) && (
            <p className={style.correctedOn}>
              Corrected on{" "}
              {formatCorrectedDate(data.correctedDate ?? item.correctedDate)}
            </p>
          )}
        </div>
        <div className={style.statusWrapper}>
          {!isEditing && (
            <>
              <Tooltip
                title={
                  actionsDisabled
                    ? PCRS_CONNECTION_REQUIRED_MESSAGE
                    : isOwn
                    ? "Edit review"
                    : NOT_OWNER_MESSAGE
                }
              >
                <span>
                  <Button
                    variant="outlineGray"
                    iconButton
                    disabled={!isOwn || actionsDisabled}
                    onClick={handleStartEdit}
                    aria-label="Edit review"
                  >
                    <Pencil size={16} />
                  </Button>
                </span>
              </Tooltip>
              <Tooltip
                title={
                  actionsDisabled
                    ? PCRS_CONNECTION_REQUIRED_MESSAGE
                    : isOwn
                    ? "Delete review"
                    : NOT_OWNER_MESSAGE
                }
              >
                <span>
                  <Button
                    variant="outlineGray"
                    iconButton
                    disabled={!isOwn || actionsDisabled}
                    onClick={() => setShowDeleteConfirm(true)}
                    aria-label="Delete review"
                  >
                    <Trash2 size={16} />
                  </Button>
                </span>
              </Tooltip>
            </>
          )}
          <Button
            variant="outlineGray"
            iconButton
            onClick={() =>
              setView({ name: views.noChronicDiseases, cdmId: null })
            }
            aria-label="Close preview"
          >
            <X size={16} />
          </Button>
        </div>
      </div>
      {(data.statusErrors?.length > 0 || data.statusWarnings?.length > 0) &&
        !isEditing && (
          <div className={style.statusMessages}>
            {data.statusErrors?.map((msg, i) => (
              <div key={i} className={style.statusBox}>
                <p className={style.statusBoxTitleError}>Rejection</p>
                <p className={style.statusBoxItemError}>{msg}</p>
              </div>
            ))}
            {data.statusWarnings?.map((msg, i) => (
              <div
                key={i}
                className={`${style.statusBox} ${style.statusBoxWarning}`}
              >
                <p className={style.statusBoxTitleWarning}>Warning</p>
                <p className={style.statusBoxItemWarning}>{msg}</p>
              </div>
            ))}
          </div>
        )}
      {isEditing ? (
        <>
          {missingPatientFields.length > 0 && (
            <div
              style={{
                alignSelf: "stretch",
                backgroundColor: "#fff3cd",
                border: "1px solid #ffc107",
                borderRadius: "12px",
                padding: "16px",
                margin: "0 24px 16px",
                color: "#856404",
                fontSize: "14px",
                fontWeight: "600",
              }}
            >
              ⚠️ OCF cannot be submitted — patient is missing{" "}
              {missingPatientFields.join(" and ")}. Please update the patient
              profile first.
            </div>
          )}
          <OcfEditSteps activeStep={editActiveStep} />
          <CdmReviewReferences patientId={patientId} />
          {editActiveStep === 1 && (
            <OcfInitialStep
              data={editData}
              onContinue={(stepData) => {
                setEditData((prev) => ({ ...prev, ...stepData }));
                setEditActiveStep(2);
              }}
              onCancel={handleCancelEdit}
              submitDisabled={missingPatientFields.length > 0}
            />
          )}
          {editActiveStep === 2 && (
            <ReviewAndExamForm
              data={editData}
              isOCF={true}
              onContinue={(newData) => {
                setEditData((prev) => ({ ...prev, ...newData }));
                setEditActiveStep(3);
              }}
              onBack={() => setEditActiveStep(1)}
            />
          )}
          {editActiveStep === 3 && (
            <TestResultsForm
              data={editData}
              onBack={() => setEditActiveStep(2)}
              onContinue={async (newData) => {
                const newDataToSend = { ...editData, ...newData };
                const res = await validateOCF(newDataToSend);

                if (res.isValid) {
                  setEditData({
                    ...newDataToSend,
                    ocfSuggestedOutcomeReason: res.suggestedOutcome.reason,
                    ocfSuggestedOutcome: res.suggestedOutcome.outcome,
                    outcome: res.suggestedOutcome.outcome || "NORMAL",
                  });
                  setEditActiveStep(4);
                } else {
                  toast.error(
                    res.validationErrors?.length > 0
                      ? res.validationErrors.join(", ")
                      : "Something went wrong"
                  );
                }
              }}
            />
          )}
          {editActiveStep === 4 && (
            <RiskAndAssessmentForm
              data={editData}
              onBack={() => setEditActiveStep(3)}
              onContinue={(newData) => {
                setEditData((prev) => ({ ...prev, ...newData }));
                setEditActiveStep(5);
              }}
            />
          )}
          {editActiveStep === 5 && (
            <OutcomeForm
              data={editData}
              onBack={() => setEditActiveStep(4)}
              isLoading={isSendingOCF}
              onContinue={async (newData) => {
                const dataToSend = { ...editData, ...newData };

                try {
                  const res = await sendOCF(dataToSend);

                  if (res?.success) {
                    toast.success("OCF updated successfully");
                    handleCancelEdit();
                    setView({ name: views.noChronicDiseases, cdmId: null });
                  } else {
                    toast.error(
                      extractSubmissionError(res, "Error while updating OCF")
                    );
                  }
                } catch (error) {
                  toast.error(
                    extractSubmissionError(error, "Error while updating OCF")
                  );
                }
              }}
            />
          )}
        </>
      ) : (
        <>
          <CdmMessagePreview xml={data.clinicalMessageXml} />
          <OcfPreviewContent data={data} />
        </>
      )}
      <Dialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title="Delete Review"
        width={480}
      >
        <div className={style.confirmBody}>
          <p className={style.confirmText}>
            Are you sure you want to delete this review? This action cannot be
            undone.
          </p>
          <div className={style.confirmActions}>
            <Button
              variant="outline"
              onClick={() => setShowDeleteConfirm(false)}
            >
              Cancel
            </Button>
            <Button onClick={() => setShowDeleteConfirm(false)}>Delete</Button>
          </div>
        </div>
      </Dialog>
    </section>
  );
};
